import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import SEO from "../components/SEO";
import SectionTitle from "../components/SectionTitle";
import PageHero from "../components/PageHero";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";
import OmSymbol from "../components/OmSymbol";
import api, { imgUrl } from "../utils/api";

const CATEGORIES = ["All", "Astrology", "Vastu Shastra", "Pooja Vidhi", "Marriage Matching", "Palmistry", "Festivals", "General"];

export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const fetchBlogs = useCallback(() => {
    setLoading(true);
    api
      .get("/blogs", { params: { category, search, page, limit: 9 } })
      .then((res) => {
        setBlogs(res.data.blogs);
        setPages(res.data.pages || 1);
      })
      .catch(() => setBlogs([]))
      .finally(() => setLoading(false));
  }, [category, search, page]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  useEffect(() => {
    setPage(1);
  }, [category, search]);

  return (
    <div>
      <SEO
        title="Blog - Astrology, Vastu & Pooja Vidhi Insights"
        description="Read insightful articles on astrology, vastu shastra, pooja vidhi, marriage matching and palmistry from Saurabh Shastri and Gauri Shastri, Thane."
        keywords="astrology blog thane, vastu tips, pooja vidhi blog, marriage matching articles"
        path="/blog"
      />

      <PageHero
        eyebrow="Knowledge Center"
        title="Our Blog"
        subtitle="Insights on astrology, vastu, pooja vidhi and Vedic traditions from our experts."
      />

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle eyebrow="Knowledge Center" title="Articles & Guidance" subtitle="" />

          <div className="flex flex-col lg:flex-row gap-4 justify-between mb-10">
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                    category === cat
                      ? "bg-saffron-500 text-white border-saffron-500"
                      : "bg-white text-gray-700 border-gold-200 hover:border-saffron-400"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="relative w-full lg:w-72">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-10 pr-4 py-2.5 rounded-full border border-gold-200 focus:outline-none focus:ring-2 focus:ring-saffron-400"
              />
            </div>
          </div>

          {loading ? (
            <Loader label="Loading articles..." />
          ) : blogs.length === 0 ? (
            <EmptyState title="No articles found" subtitle="Try a different category or search term, or check back soon for new content." />
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {blogs.map((b) => (
                  <Link
                    key={b._id}
                    to={`/blog/${b.slug}`}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gold-100 transition-shadow group"
                  >
                    <div className="aspect-[16/10] bg-gold-50 overflow-hidden">
                      {b.coverImage ? (
                        <img src={imgUrl(b.coverImage)} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-5xl text-saffron-400"><OmSymbol size={48} /></div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-saffron-600 font-semibold uppercase tracking-wide">{b.category}</span>
                        <span className="text-xs text-gray-400">{b.author}</span>
                      </div>
                      <h4 className="font-heading text-maroon-800 text-lg mb-2 line-clamp-2">{b.title}</h4>
                      <p className="text-sm text-gray-600 line-clamp-3">{b.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>

              {pages > 1 && (
                <div className="flex justify-center gap-2 mt-10">
                  {[...Array(pages)].map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setPage(idx + 1)}
                      className={`w-9 h-9 rounded-full text-sm font-medium ${
                        page === idx + 1 ? "bg-maroon-700 text-gold-100" : "bg-white border border-gold-200 text-gray-700"
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
